import type { i18n } from "i18next";
import { useCallback, useSyncExternalStore } from "react";
import type { ManagedRedirectLabels } from "./ManagedRedirectCard.tsx";

export type ManagedRedirectDestination = "classic" | "performance";

const DESTINATION_NAMES: Record<ManagedRedirectDestination, string> = {
    classic: "Workleap Classic",
    performance: "Workleap Performance"
};

const LABELS: Record<string, (appName: string) => ManagedRedirectLabels> = {
    en: appName => ({
        goThereNow: "Go there now",
        modalEyebrow: "Heads up",
        modalTitle: "One quick detour",
        modalBody: `This setting isn't available in the new app yet. We'll take you to ${appName} to manage it. Nothing will be lost.`,
        modalCancelLabel: "Cancel",
        modalContinueLabel: `Continue in ${appName}`
    }),
    fr: appName => ({
        goThereNow: "Y aller maintenant",
        modalEyebrow: "À noter",
        modalTitle: "Un petit détour",
        modalBody: `Ce paramètre n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers ${appName} pour le gérer. Rien ne sera perdu.`,
        modalCancelLabel: "Annuler",
        modalContinueLabel: `Continuer dans ${appName}`
    })
};

export function useManagedRedirectLabels(i18nextInstance: i18n, destination: ManagedRedirectDestination = "classic"): ManagedRedirectLabels {
    const subscribe = useCallback(
        (onLanguageChange: () => void) => {
            i18nextInstance.on("languageChanged", onLanguageChange);

            return () => {
                i18nextInstance.off("languageChanged", onLanguageChange);
            };
        },
        [i18nextInstance]
    );
    const language = useSyncExternalStore(subscribe, () => i18nextInstance.language);
    const baseLanguage = language.split("-")[0];
    const buildLabels = LABELS[baseLanguage] ?? LABELS.en;

    return buildLabels(DESTINATION_NAMES[destination]);
}
